import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NavItemId = string;

export type NavBadgeSource = 'unread_count' | 'task_count' | null;

export interface NavItem {
  id: NavItemId;
  route: string;
  icon: string;
  label_key: string;
  badge?: NavBadgeSource;
  requires_addon?: string;
  requires_permission?: string;
  requiresBroker?: boolean;
  roles?: string[];
  is_new?: boolean;
  /** Custom external link — user-created item */
  external?: boolean;
  /** Override label for custom items */
  label?: string;
}

export interface NavSection {
  id: string;
  label_key: string;
  collapsible: boolean;
  default_open: boolean;
  items: NavItem[];
}

export interface NavTreeDeltaItem {
  id: string;
  hidden?: boolean;
  /** Custom external link fields */
  label?: string;
  route?: string;
  icon?: string;
  external?: boolean;
}

export interface NavTreeDeltaSection {
  id: string;
  hidden?: boolean;
  label?: string;
  items?: NavTreeDeltaItem[];
}

export interface NavTreeDelta {
  version: 1;
  sections: NavTreeDeltaSection[];
}

export interface ResolvedNavItem extends NavItem {
  hidden: false;
}

export interface ResolvedNavSection {
  id: string;
  label_key: string;
  label?: string;
  collapsible: boolean;
  default_open: boolean;
  items: ResolvedNavItem[];
}

export const HR_MY_NAV_ENTRIES = [
  {
    id: 'hr-promotions',
    path: '/app/hr/promotions',
    labelKey: 'nav.promotions',
    section: 'my',
    roles: ['hr_staff', 'hr_manager'],
  },
  {
    id: 'hr-notifications',
    path: '/app/hr/notifications',
    labelKey: 'nav.notifications',
    section: 'my',
    roles: ['hr_staff', 'hr_manager'],
  },
] as const;

// ---------------------------------------------------------------------------
// NAV_REGISTRY — single source of truth for all routes
// ---------------------------------------------------------------------------

export const NAV_REGISTRY: NavSection[] = [
  {
    id: 'main',
    label_key: 'nav.sections.main',
    collapsible: false,
    default_open: true,
    items: [
      { id: 'home', route: '/home', icon: 'LayoutDashboard', label_key: 'nav.home' },
      {
        id: 'inbox',
        route: '/inbox',
        icon: 'Inbox',
        label_key: 'nav.inbox',
        badge: 'unread_count',
      },
      {
        id: 'announcements',
        route: '/announcements',
        icon: 'Megaphone',
        label_key: 'nav.announcements',
      },
      { id: 'calendar', route: '/calendar', icon: 'CalendarDays', label_key: 'nav.calendar' },
      {
        id: 'billing',
        route: '/app/billing',
        icon: 'CreditCard',
        label_key: 'sidebar.billing',
        roles: ['*'],
      },
      {
        id: 'company-profile',
        route: '/app/settings?tab=company',
        icon: 'Building2',
        label_key: 'sidebar.company_profile',
        roles: ['*'],
      },
      {
        id: 'admin-billing',
        route: '/app/admin/billing',
        icon: 'BarChart3',
        label_key: 'sidebar.admin_billing',
        roles: ['platform_admin'],
      },
      {
        id: 'tb_company_browse',
        route: '/app/hr/talent-broker',
        icon: 'WalletCards',
        label_key: 'talentBroker.nav.marketplace',
        roles: ['company_admin', 'hr_manager'],
      },
      {
        id: 'tb_company_inquiries',
        route: '/app/hr/talent-broker/inquiries',
        icon: 'MessageSquare',
        label_key: 'talentBroker.nav.inquiries',
        roles: ['company_admin', 'hr_manager'],
      },
      {
        id: 'tb_company_wallet',
        route: '/app/hr/talent-broker/wallet',
        icon: 'Wallet',
        label_key: 'talentBroker.nav.wallet',
        roles: ['company_admin', 'hr_manager'],
      },
      {
        id: 'tb_company_sourcing',
        route: '/app/hr/talent-broker/sourcing-requests',
        icon: 'Megaphone',
        label_key: 'talentBroker.sourcingRequests.nav.requests',
        roles: ['company_admin', 'hr_manager'],
      },
      {
        id: 'tb_broker_dashboard',
        route: '/app/freelancer/broker',
        icon: 'UserRoundCheck',
        label_key: 'talentBroker.nav.marketplace',
        requiresBroker: true,
        roles: ['freelancer'],
      },
      {
        id: 'tb_broker_candidates',
        route: '/app/freelancer/broker/candidates',
        icon: 'Users',
        label_key: 'talentBroker.nav.candidatePool',
        requiresBroker: true,
        roles: ['freelancer'],
      },
      {
        id: 'tb_broker_listings',
        route: '/app/freelancer/broker/listings',
        icon: 'BriefcaseBusiness',
        label_key: 'talentBroker.nav.myListings',
        requiresBroker: true,
        roles: ['freelancer'],
      },
      {
        id: 'tb_broker_inquiries',
        route: '/app/freelancer/broker/inquiries',
        icon: 'MessageSquare',
        label_key: 'talentBroker.nav.inquiries',
        requiresBroker: true,
        roles: ['freelancer'],
      },
      {
        id: 'tb_find_requests',
        route: '/app/freelancer/broker/find-requests',
        icon: 'Megaphone',
        label_key: 'talentBroker.sourcingRequests.nav.findRequests',
        requiresBroker: true,
        roles: ['freelancer'],
      },
      {
        id: 'tb_my_submissions',
        route: '/app/freelancer/broker/my-submissions',
        icon: 'Send',
        label_key: 'talentBroker.sourcingRequests.nav.mySubmissions',
        requiresBroker: true,
        roles: ['freelancer'],
      },
    ],
  },
  {
    id: 'team',
    label_key: 'nav.sections.team',
    collapsible: true,
    default_open: true,
    items: [
      { id: 'people', route: '/people', icon: 'Users', label_key: 'nav.people' },
      { id: 'org-chart', route: '/org-chart', icon: 'Network', label_key: 'nav.org_chart' },
      {
        id: 'recruitment',
        route: '/recruitment',
        icon: 'UserSearch',
        label_key: 'nav.recruitment',
        requires_addon: 'recruitment',
      },
      { id: 'invitations', route: '/invitations', icon: 'UserPlus', label_key: 'nav.invitations' },
    ],
  },
  {
    id: 'my',
    label_key: 'nav.sections.my',
    collapsible: true,
    default_open: true,
    items: [
      {
        id: 'hr-promotions',
        route: '/app/hr/promotions',
        icon: 'Gift',
        label_key: 'nav.promotions',
        roles: ['hr_staff', 'hr_manager'],
      },
      {
        id: 'hr-notifications',
        route: '/app/hr/notifications',
        icon: 'Bell',
        label_key: 'nav.notifications',
        roles: ['hr_staff', 'hr_manager'],
      },
    ],
  },
  {
    id: 'work',
    label_key: 'nav.sections.work',
    collapsible: true,
    default_open: true,
    items: [
      {
        id: 'tasks',
        route: '/tasks',
        icon: 'CheckSquare',
        label_key: 'nav.tasks',
        badge: 'task_count',
      },
      {
        id: 'reminders',
        route: '/reminders',
        icon: 'BellClock',
        label_key: 'nav.reminders',
      },
      {
        id: 'projects',
        route: '/projects',
        icon: 'FolderKanban',
        label_key: 'nav.projects',
        requires_addon: 'projects',
      },
      {
        id: 'goals',
        route: '/goals',
        icon: 'Target',
        label_key: 'nav.goals',
        requires_addon: 'goals',
      },
      {
        id: 'performance',
        route: '/performance',
        icon: 'TrendingUp',
        label_key: 'nav.performance',
        requires_addon: 'performance-reviews',
      },
      {
        id: 'automations',
        route: '/automations',
        icon: 'Workflow',
        label_key: 'nav.automations',
        requires_addon: 'workflow-automation',
      },
      {
        id: 'automations_runs',
        route: '/automations/runs',
        icon: 'Activity',
        label_key: 'nav.automations_runs',
        requires_addon: 'workflow-automation',
      },
    ],
  },
  {
    id: 'hr',
    label_key: 'nav.sections.hr',
    collapsible: true,
    default_open: false,
    items: [
      {
        id: 'leave-management',
        route: '/leave-management',
        icon: 'CalendarOff',
        label_key: 'nav.leave',
        requires_addon: 'leave-management',
      },
      { id: 'vacations', route: '/vacations', icon: 'Palmtree', label_key: 'nav.vacations' },
      { id: 'wfh', route: '/wfh', icon: 'Home', label_key: 'nav.wfh', requires_addon: 'wfh' },
      {
        id: 'overtime',
        route: '/overtime',
        icon: 'Clock',
        label_key: 'nav.overtime',
        requires_addon: 'overtime-requests',
      },
      {
        id: 'schedules',
        route: '/schedules',
        icon: 'CalendarClock',
        label_key: 'nav.schedules',
        requires_addon: 'employee-schedules',
      },
      {
        id: 'time-tracking',
        route: '/time-tracking',
        icon: 'Timer',
        label_key: 'nav.time_tracking',
        requires_addon: 'time-tracking',
      },
      {
        id: 'attendance-report',
        route: '/addons/time-tracking/report',
        icon: 'BarChart2',
        label_key: 'nav.attendance_report',
        requires_addon: 'time-tracking',
      },
      {
        id: 'disciplinary',
        route: '/disciplinary',
        icon: 'AlertTriangle',
        label_key: 'nav.disciplinary',
        requires_addon: 'disciplinary',
      },
      {
        id: 'payroll',
        route: '/payroll',
        icon: 'Banknote',
        label_key: 'nav.payroll',
        requires_addon: 'payroll',
      },
      {
        id: 'salary-requests',
        route: '/salary-requests',
        icon: 'ArrowUpCircle',
        label_key: 'nav.salary_requests',
        requires_addon: 'salary-requests',
      },
      {
        id: 'job-grades',
        route: '/job-grades',
        icon: 'Layers',
        label_key: 'nav.job_grades',
        requires_addon: 'job-grades',
      },
      {
        id: 'benefits',
        route: '/benefits',
        icon: 'Gift',
        label_key: 'nav.benefits',
        requires_addon: 'benefits',
      },
    ],
  },
  {
    id: 'crm',
    label_key: 'nav.sections.crm',
    collapsible: true,
    default_open: false,
    items: [
      {
        id: 'contacts',
        route: '/contacts',
        icon: 'BookUser',
        label_key: 'nav.contacts',
        requires_addon: 'crm-contacts',
      },
      {
        id: 'crm-deduplication',
        route: '/addons/crm-deduplication',
        icon: 'GitMerge',
        label_key: 'nav.crm_deduplication',
        requires_addon: 'crm-contacts',
      },
      {
        id: 'crm-lead-scoring',
        route: '/addons/crm-lead-scoring',
        icon: 'TrendingUp',
        label_key: 'nav.crm_lead_scoring',
        requires_addon: 'crm-leads',
      },
      { id: 'quotes', route: '/quotes', icon: 'FileText', label_key: 'nav.quotes' },
      {
        id: 'suppliers',
        route: '/suppliers',
        icon: 'Truck',
        label_key: 'nav.suppliers',
        requires_addon: 'invoicing',
      },
      {
        id: 'procurement',
        route: '/addons/procurement',
        icon: 'ShoppingCart',
        label_key: 'nav.procurement',
        requires_addon: 'procurement',
      },
      {
        id: 'procurement-requisitions',
        route: '/procurement/requisitions',
        icon: 'ClipboardList',
        label_key: 'nav.procurement_requisitions',
        requires_addon: 'procurement',
      },
      {
        id: 'procurement-rfq',
        route: '/procurement/rfq',
        icon: 'Send',
        label_key: 'nav.procurement_rfq',
        requires_addon: 'procurement',
      },
      {
        id: 'procurement-orders',
        route: '/procurement/orders',
        icon: 'ShoppingCart',
        label_key: 'nav.procurement_orders',
        requires_addon: 'procurement',
      },
      {
        id: 'procurement-receipts',
        route: '/procurement/receipts',
        icon: 'Receipt',
        label_key: 'nav.procurement_receipts',
        requires_addon: 'procurement',
      },
      {
        id: 'procurement-vendors',
        route: '/procurement/vendors',
        icon: 'Truck',
        label_key: 'nav.procurement_vendors',
        requires_addon: 'procurement',
      },
      {
        id: 'procurement-approvals',
        route: '/procurement/approvals',
        icon: 'CheckSquare',
        label_key: 'nav.procurement_approvals',
        requires_addon: 'procurement',
      },
      {
        id: 'expenses',
        route: '/expenses',
        icon: 'Receipt',
        label_key: 'nav.expenses',
        requires_addon: 'expenses',
      },
      {
        id: 'expenses-approvals',
        route: '/expenses/approvals',
        icon: 'CheckSquare',
        label_key: 'nav.expenses_approvals',
        requires_addon: 'expenses',
      },
      {
        id: 'expenses-reimbursements',
        route: '/expenses/reimbursements',
        icon: 'ArrowLeftRight',
        label_key: 'nav.expenses_reimbursements',
        requires_addon: 'expenses',
      },
    ],
  },
  {
    id: 'documents',
    label_key: 'nav.sections.documents',
    collapsible: true,
    default_open: false,
    items: [
      { id: 'documents', route: '/documents', icon: 'FileStack', label_key: 'nav.documents' },
      { id: 'files', route: '/files', icon: 'FolderOpen', label_key: 'nav.files' },
      {
        id: 'policies',
        route: '/policies',
        icon: 'ShieldCheck',
        label_key: 'nav.policies',
        requires_addon: 'policies',
      },
      {
        id: 'assets',
        route: '/assets',
        icon: 'Package',
        label_key: 'nav.assets',
        requires_addon: 'asset-management',
      },
    ],
  },
  {
    id: 'learning',
    label_key: 'nav.sections.learning',
    collapsible: true,
    default_open: false,
    items: [
      {
        id: 'training',
        route: '/training',
        icon: 'GraduationCap',
        label_key: 'nav.training',
        requires_addon: 'training',
      },
      {
        id: 'surveys',
        route: '/surveys',
        icon: 'ClipboardCheck',
        label_key: 'nav.surveys',
        requires_addon: 'surveys',
      },
      {
        id: 'helpdesk',
        route: '/helpdesk',
        icon: 'LifeBuoy',
        label_key: 'nav.helpdesk',
        requires_addon: 'helpdesk',
      },
      { id: 'tickets', route: '/tickets', icon: 'Ticket', label_key: 'nav.tickets' },
    ],
  },
  {
    id: 'reports',
    label_key: 'nav.sections.reports',
    collapsible: true,
    default_open: false,
    items: [
      { id: 'reports', route: '/reports', icon: 'BarChart2', label_key: 'nav.reports' },
      {
        id: 'reports-headcount',
        route: '/reports/headcount',
        icon: 'Users',
        label_key: 'nav.reports_headcount',
        requires_permission: 'reports:read',
      },
      {
        id: 'reports-payroll',
        route: '/reports/payroll',
        icon: 'Banknote',
        label_key: 'nav.reports_payroll',
        requires_permission: 'reports:read',
      },
      {
        id: 'reports-attendance',
        route: '/reports/attendance',
        icon: 'Clock',
        label_key: 'nav.reports_attendance',
        requires_permission: 'reports:read',
      },
      {
        id: 'reports-cohort',
        route: '/reports/cohort',
        icon: 'Users',
        label_key: 'nav.reports_cohort',
      },
      {
        id: 'reports-funnel',
        route: '/reports/funnel',
        icon: 'Filter',
        label_key: 'nav.reports_funnel',
      },
      {
        id: 'reports-predictive',
        route: '/reports/predictive',
        icon: 'LineChart',
        label_key: 'nav.reports_predictive',
      },
    ],
  },
  {
    id: 'settings-roles',
    label_key: 'nav.sections.settings_roles',
    collapsible: false,
    default_open: false,
    items: [
      {
        id: 'settings-roles-list',
        route: '/settings/roles',
        icon: 'Shield',
        label_key: 'nav.settings_roles',
        requires_permission: 'roles:write',
      },
      {
        id: 'settings-role-ui-elements',
        route: '/settings/roles/:roleId/ui-elements',
        icon: 'Shield',
        label_key: 'nav.settings_role_ui_elements',
        requires_permission: 'roles:write',
      },
    ],
  },
  {
    id: 'settings-security',
    label_key: 'nav.sections.settings_security',
    collapsible: false,
    default_open: false,
    items: [
      {
        id: 'settings-security-page',
        route: '/settings/security',
        icon: 'ShieldCheck',
        label_key: 'nav.settings_security',
      },
      {
        id: 'settings-mfa-page',
        route: '/settings/mfa',
        icon: 'Smartphone',
        label_key: 'nav.settings_mfa',
      },
    ],
  },
  {
    id: 'settings-developer',
    label_key: 'nav.sections.settings_developer',
    collapsible: false,
    default_open: false,
    items: [
      {
        id: 'settings-developer-page',
        route: '/settings/developer',
        icon: 'Key',
        label_key: 'nav.settings_developer',
        requires_permission: 'settings:api_keys:read',
      },
      {
        id: 'settings-integrations-page',
        route: '/settings/integrations',
        icon: 'Key',
        label_key: 'nav.settings_integrations',
        requires_permission: 'settings:api_keys:read',
      },
      {
        id: 'settings-rules-page',
        route: '/settings/rules',
        icon: 'GitBranch',
        label_key: 'nav.settings_rules',
        requires_permission: 'settings:api_keys:read',
      },
      {
        id: 'settings-ai',
        route: '/settings/ai',
        icon: 'Bot',
        label_key: 'nav.settings_ai',
        requires_permission: 'settings:api_keys:read',
      },
    ],
  },
  {
    id: 'settings-addons',
    label_key: 'nav.sections.settings_addons',
    collapsible: false,
    default_open: false,
    items: [
      {
        id: 'settings-addons-page',
        route: '/settings/tenant/addons',
        icon: 'Puzzle',
        label_key: 'nav.settings_addons',
        requires_permission: 'settings:addons:read',
      },
    ],
  },
  // "addons" and "admin" sections are injected at runtime — not hardcoded here
];

// ---------------------------------------------------------------------------
// resolveNavTree — pure function, no async
// Merges user delta → tenant delta → NAV_REGISTRY
// ---------------------------------------------------------------------------

// Items that were first-run / setup-only and should be silently dropped from
// any stored tenant or user nav delta (old stored customizations may still
// reference them; do not re-inject them into the resolved tree).
const FIRST_RUN_ITEM_IDS = new Set(['onboarding', 'orientation']);

export function resolveNavTree(
  tenantDelta: NavTreeDelta | null,
  userDelta: NavTreeDelta | null,
  activeAddonIds: string[],
  userRole: string | null,
): ResolvedNavSection[] {
  const addonSet = new Set(activeAddonIds);

  // Build a lookup from all deltas: section_id -> delta section
  const tenantSectionMap = new Map<string, NavTreeDeltaSection>(
    (tenantDelta?.sections ?? []).map((s) => [s.id, s]),
  );
  const userSectionMap = new Map<string, NavTreeDeltaSection>(
    (userDelta?.sections ?? []).map((s) => [s.id, s]),
  );

  // Determine section order: user delta order → tenant delta order → registry order
  const orderedSectionIds = mergeOrder(
    (userDelta?.sections ?? []).map((s) => s.id),
    (tenantDelta?.sections ?? []).map((s) => s.id),
    NAV_REGISTRY.map((s) => s.id),
  );

  const result: ResolvedNavSection[] = [];

  for (const sectionId of orderedSectionIds) {
    const registrySection = NAV_REGISTRY.find((s) => s.id === sectionId);
    const tenantSec = tenantSectionMap.get(sectionId);
    const userSec = userSectionMap.get(sectionId);

    // Hidden check: user wins over tenant wins over default (not hidden)
    const hidden = userSec?.hidden ?? tenantSec?.hidden ?? false;
    if (hidden) continue;

    // Custom section (no registry entry, from delta only)
    if (!registrySection) {
      // Only include if both tenant and user didn't mark it hidden
      // and there's a label to show
      const label = userSec?.label ?? tenantSec?.label;
      if (!label) continue;

      const items = resolveCustomItems(userSec, tenantSec);
      result.push({
        id: sectionId,
        label_key: `nav.sections.${sectionId}`,
        label,
        collapsible: true,
        default_open: false,
        items,
      });
      continue;
    }

    // Resolve items for registry section
    const userItemMap = new Map<string, NavTreeDeltaItem>(
      (userSec?.items ?? []).map((i) => [i.id, i]),
    );
    const tenantItemMap = new Map<string, NavTreeDeltaItem>(
      (tenantSec?.items ?? []).map((i) => [i.id, i]),
    );

    // Determine item order: user → tenant → registry
    const orderedItemIds = mergeOrder(
      (userSec?.items ?? []).map((i) => i.id),
      (tenantSec?.items ?? []).map((i) => i.id),
      registrySection.items.map((i) => i.id),
    );

    const resolvedItems: ResolvedNavItem[] = [];
    for (const itemId of orderedItemIds) {
      const registryItem = registrySection.items.find((i) => i.id === itemId);
      const tenantItem = tenantItemMap.get(itemId);
      const userItem = userItemMap.get(itemId);

      const itemHidden = userItem?.hidden ?? tenantItem?.hidden ?? false;
      if (itemHidden) continue;

      if (!registryItem) {
        // Custom item in a registry section — drop first-run items, include rest if has a route
        if (FIRST_RUN_ITEM_IDS.has(itemId)) continue;
        const customRoute = userItem?.route ?? tenantItem?.route;
        if (!customRoute) continue;
        resolvedItems.push({
          id: itemId,
          route: customRoute,
          icon: userItem?.icon ?? tenantItem?.icon ?? 'ExternalLink',
          label_key: `nav.${itemId}`,
          label: userItem?.label ?? tenantItem?.label,
          external: userItem?.external ?? tenantItem?.external ?? false,
          hidden: false,
        });
        continue;
      }

      // Filter by addon requirement
      if (registryItem.requires_addon && !addonSet.has(registryItem.requires_addon)) continue;

      // Filter by permission (role check — basic)
      if (registryItem.requires_permission) {
        const isAdmin = userRole === 'admin' || userRole === 'owner';
        if (!isAdmin) continue;
      }

      if (registryItem.roles && userRole && !registryItem.roles.includes(userRole)) continue;

      resolvedItems.push({ ...registryItem, hidden: false });
    }

    if (resolvedItems.length === 0 && registrySection.items.length > 0) continue;

    result.push({
      id: sectionId,
      label_key: registrySection.label_key,
      label: userSec?.label ?? tenantSec?.label,
      collapsible: registrySection.collapsible,
      default_open: registrySection.default_open,
      items: resolvedItems,
    });
  }

  return result;
}

function resolveCustomItems(
  userSec: NavTreeDeltaSection | undefined,
  tenantSec: NavTreeDeltaSection | undefined,
): ResolvedNavItem[] {
  const seen = new Set<string>();
  const items: ResolvedNavItem[] = [];
  const allItems = [...(userSec?.items ?? []), ...(tenantSec?.items ?? [])];
  for (const item of allItems) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    if (FIRST_RUN_ITEM_IDS.has(item.id)) continue;
    if (item.hidden) continue;
    if (!item.route) continue;
    items.push({
      id: item.id,
      route: item.route,
      icon: item.icon ?? 'ExternalLink',
      label_key: `nav.${item.id}`,
      label: item.label,
      external: item.external ?? false,
      hidden: false,
    });
  }
  return items;
}

/** Merge ordered id lists: first wins for position, latecomers appended in order */
function mergeOrder(first: string[], second: string[], third: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of [...first, ...second, ...third]) {
    if (!seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// useTenantNavConfig — fetches + saves tenant admin nav override
// ---------------------------------------------------------------------------

const TENANT_NAV_KEYS = (tenantId: string) => ['tenant-nav-config', tenantId] as const;

async function fetchTenantNavConfig(supabase: SupabaseClientLike): Promise<NavTreeDelta | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token ?? '';
  const res = await fetch('/functions/v1/settings/nav-config', {
    headers: { authorization: `Bearer ${token}` },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<NavTreeDelta>;
}

async function saveTenantNavConfigApi(
  supabase: SupabaseClientLike,
  navTree: NavTreeDelta,
): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token ?? '';
  const res = await fetch('/functions/v1/settings/nav-config', {
    method: 'PUT',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify(navTree),
  });
  if (!res.ok) throw new Error(await res.text());
}

export interface UseTenantNavConfigResult {
  data: NavTreeDelta | null;
  isLoading: boolean;
  save: (navTree: NavTreeDelta) => Promise<void>;
}

export function useTenantNavConfig(
  supabase: SupabaseClientLike,
  tenantId: string,
): UseTenantNavConfigResult {
  const qc = useQueryClient();
  const { data = null, isLoading } = useQuery({
    queryKey: TENANT_NAV_KEYS(tenantId),
    queryFn: () => fetchTenantNavConfig(supabase),
    staleTime: 5 * 60 * 1000,
  });

  const save = useCallback(
    async (navTree: NavTreeDelta) => {
      qc.setQueryData(TENANT_NAV_KEYS(tenantId), navTree);
      await saveTenantNavConfigApi(supabase, navTree);
    },
    [supabase, tenantId, qc],
  );

  return { data, isLoading, save };
}

// ---------------------------------------------------------------------------
// useUserNavTree — per-user nav override (follows useUserPreference pattern)
// ---------------------------------------------------------------------------

const USER_PREF_KEYS = ['preferences'] as const;

async function fetchPreferences(supabase: SupabaseClientLike): Promise<Record<string, unknown>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token ?? '';
  const res = await fetch('/functions/v1/settings/preferences', {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<Record<string, unknown>>;
}

async function patchNavPreference(
  supabase: SupabaseClientLike,
  value: NavTreeDelta | null,
): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token ?? '';
  const res = await fetch(`/functions/v1/settings/preferences/${encodeURIComponent('nav_tree')}`, {
    method: 'PATCH',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ value }),
  });
  if (!res.ok) throw new Error(await res.text());
}

export function useUserNavTree(
  supabase: SupabaseClientLike,
): [NavTreeDelta | null, (v: NavTreeDelta | null) => void] {
  const qc = useQueryClient();
  const { data: allPrefs } = useQuery({
    queryKey: USER_PREF_KEYS,
    queryFn: () => fetchPreferences(supabase),
    staleTime: 5 * 60 * 1000,
  });

  const stored = (allPrefs?.nav_tree as NavTreeDelta | null | undefined) ?? null;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryRef = useRef(0);

  const setValue = useCallback(
    (v: NavTreeDelta | null) => {
      qc.setQueryData(USER_PREF_KEYS, (old: Record<string, unknown> | undefined) => ({
        ...(old ?? {}),
        nav_tree: v,
      }));

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        retryRef.current = 0;
        const attempt = async () => {
          try {
            await patchNavPreference(supabase, v);
            retryRef.current = 0;
          } catch {
            retryRef.current += 1;
            if (retryRef.current < 3) {
              setTimeout(() => void attempt(), 1500 * retryRef.current);
            }
          }
        };
        void attempt();
      }, 500);
    },
    [supabase, qc],
  );

  return [stored, setValue];
}

// ---------------------------------------------------------------------------
// useResolvedNavTree — composes tenant config + user tree + resolver
// ---------------------------------------------------------------------------

export function useResolvedNavTree(
  supabase: SupabaseClientLike,
  tenantId: string,
  activeAddonIds: string[],
  userRole: string | null,
): ResolvedNavSection[] {
  const { data: tenantDelta } = useTenantNavConfig(supabase, tenantId);
  const [userDelta] = useUserNavTree(supabase);
  return resolveNavTree(tenantDelta ?? null, userDelta, activeAddonIds, userRole);
}
